import {Injectable, OnDestroy} from '@angular/core';
import {HttpClient, HttpErrorResponse} from '@angular/common/http';
import {Subscription} from 'rxjs/Subscription';
import {Subject} from 'rxjs/Subject';
import {ApiConnectService} from './api-connect.service';
import {AuthService} from './auth.service';
import {ContentTranslation} from '../models/content-translation.model';

const ONBOARDING_PATH = '/common/api/v2/users/me/onboarding';
const COMPLETION_PATH = '/complete';
const PAGE_QUERY = '?standardsAfter=';
const PAGE_SIZE_QUERY = '&standardsLimit=20';
const CONFLICT = 409;
const INITIAL_REVISION = 0;
const BOOLEAN_TYPE = 'boolean';

export interface OnboardingTraining {
  specializationId: string;
  name: string;
  nameTranslation?: ContentTranslation;
  requiredLevel: number;
  currentLevel: number;
  companyRequired: boolean;
  currentHeadRequired: boolean;
  departmentSources: {id: string; name: string}[];
  ownerGroupId?: string;
  ownerGroupName?: string;
  ownerGroupType?: string;
}

export interface OnboardingStandard {
  id: string;
  number: string;
  ruName: string;
  enName: string;
  nameTranslation?: ContentTranslation;
  language: string;
  pinPriority: number;
  completed: boolean;
}

export interface EmployeeOnboardingSnapshot {
  userId: string;
  approved: boolean;
  approvedAt: string;
  employmentDate: string;
  eligibleAt: string;
  requiredTrainingCount: number;
  completedTrainingCount: number;
  trainings: OnboardingTraining[];
  headTrainings?: OnboardingTraining[];
  assignedStandardsCount: number;
  completedStandardsCount: number;
  startingStandards: OnboardingStandard[];
  onboardingCompletedAt?: string;
  onboardingVisibleUntil?: string;
  onboardingCompletionEligible?: boolean;
  onboardingHistoricallySkipped?: boolean;
  probationCompletedAt?: string;
  probationVisibleUntil?: string;
  approvalSource?: string;
  remainingStandards?: OnboardingStandardsPage;
  remainingStandardsCount?: number;
  completedRemainingStandardsCount?: number;
}

export interface OnboardingStandardsPage {
  items: OnboardingStandard[];
  nextCursor: string;
  hasMore: boolean;
}

@Injectable()
export class EmployeeOnboardingService implements OnDestroy {
  readonly changes$ = new Subject<void>();
  snapshot: EmployeeOnboardingSnapshot;
  loading = false;
  failed = false;
  completing = false;
  completionFailed = false;
  pageLoading = false;
  pageFailed = false;
  private active = false;
  private request: Subscription;
  private sessionClear: Subscription;
  private pageRequest: Subscription;
  private completionRequest: Subscription;
  private revision = INITIAL_REVISION;
  private sessionRevision = INITIAL_REVISION;
  private completionAttempted = false;

  constructor(private http: HttpClient, private api: ApiConnectService, auth: AuthService) {
    this.sessionClear = auth.onSessionCleared$.subscribe(() => this.clear());
  }

  ngOnDestroy(): void {
    this.clear();
    this.sessionClear.unsubscribe();
  }

  start(): void {
    this.active = true;
    this.invalidate();
  }

  invalidate(): void {
    if (!this.active) { return; }
    this.cancelRequest();
    this.cancelPage();
    const revision = ++this.revision;
    this.loading = true;
    this.failed = false;
    this.request = this.http.get<EmployeeOnboardingSnapshot>(this.api.baseApiUrl + ONBOARDING_PATH)
      .subscribe(snapshot => {
        if (revision === this.revision) { this.accept(snapshot); }
      }, () => {
        if (revision === this.revision) { this.reject(); }
      });
  }

  clear(): void {
    this.active = false;
    this.revision++;
    this.sessionRevision++;
    this.cancelRequest();
    this.cancelPage();
    if (this.completionRequest) { this.completionRequest.unsubscribe(); }
    this.completing = false;
    this.completionFailed = false;
    this.completionAttempted = false;
    this.snapshot = null;
    this.loading = false;
    this.failed = false;
    this.changes$.next();
  }

  private accept(snapshot: EmployeeOnboardingSnapshot): void {
    this.loading = false;
    this.failed = !snapshot || typeof snapshot.onboardingHistoricallySkipped !== BOOLEAN_TYPE;
    if (this.failed) { return; }
    this.snapshot = snapshot;
    if (!snapshot.onboardingCompletionEligible) {
      this.completionAttempted = false;
      this.completionFailed = false;
    }
    this.tryComplete();
    this.changes$.next();
  }

  retryCompletion(): void {
    this.completionAttempted = false;
    this.completionFailed = false;
    this.invalidate();
  }

  loadMore(): void {
    const page = this.snapshot && this.snapshot.remainingStandards;
    if (!this.active || this.loading || this.pageLoading || !page || !page.hasMore) { return; }
    this.pageLoading = true;
    this.pageFailed = false;
    const revision = this.revision;
    const url = this.api.baseApiUrl + ONBOARDING_PATH + PAGE_QUERY
      + encodeURIComponent(page.nextCursor) + PAGE_SIZE_QUERY;
    this.pageRequest = this.http.get<EmployeeOnboardingSnapshot>(url).subscribe(
      snapshot => this.acceptPage(snapshot, revision),
      () => this.rejectPage(revision));
  }

  private acceptPage(snapshot: EmployeeOnboardingSnapshot, revision: number): void {
    if (revision !== this.revision || !this.snapshot) { return; }
    this.pageLoading = false;
    if (!snapshot || !snapshot.remainingStandards || snapshot.userId !== this.snapshot.userId) {
      this.pageFailed = true;
      return;
    }
    const items = this.snapshot.remainingStandards.items;
    const known = new Set(items.map(item => item.id));
    snapshot.remainingStandards.items = items.concat(snapshot.remainingStandards.items.filter(item => !known.has(item.id)));
    this.snapshot = snapshot;
    this.tryComplete();
    this.changes$.next();
  }

  private rejectPage(revision: number): void {
    if (revision !== this.revision) { return; }
    this.pageLoading = false;
    this.pageFailed = true;
    this.changes$.next();
  }

  private tryComplete(): void {
    if (!this.active || this.completing || this.completionAttempted || !this.snapshot
      || !this.snapshot.onboardingCompletionEligible || this.snapshot.onboardingCompletedAt
      || this.snapshot.onboardingHistoricallySkipped) { return; }
    this.completing = true;
    this.completionFailed = false;
    this.completionAttempted = true;
    const session = this.sessionRevision;
    this.completionRequest = this.http.post<EmployeeOnboardingSnapshot>(
      this.api.baseApiUrl + ONBOARDING_PATH + COMPLETION_PATH, {}).subscribe(
      snapshot => this.complete(snapshot, session), error => this.rejectCompletion(error, session));
  }

  private complete(snapshot: EmployeeOnboardingSnapshot, session: number): void {
    if (session !== this.sessionRevision || !this.active) { return; }
    this.completing = false;
    if (!snapshot || !snapshot.onboardingCompletedAt) {
      this.completionFailed = true;
      return;
    }
    this.cancelRequest();
    this.cancelPage();
    this.revision++;
    this.accept(snapshot);
  }

  private rejectCompletion(error: HttpErrorResponse, session: number): void {
    if (session !== this.sessionRevision || !this.active) { return; }
    this.completing = false;
    this.completionFailed = true;
    if (error && error.status === CONFLICT) {
      this.invalidate();
    }
  }

  private reject(): void {
    this.loading = false;
    this.failed = true;
  }

  private cancelRequest(): void {
    if (this.request) { this.request.unsubscribe(); }
  }

  private cancelPage(): void {
    if (this.pageRequest) { this.pageRequest.unsubscribe(); }
    this.pageLoading = false;
    this.pageFailed = false;
  }
}



// WEBPACK FOOTER //
// C:/work/internal-frontend/src/app/services/employee-onboarding.service.ts